import React, { useState, useEffect, useRef } from 'react';
import { TabBar, SafeArea } from 'antd-mobile';
import LogoDesktop from 'assets/images/logo-color.svg';
import LogoMobile from 'assets/images/logo.svg';
import { ReactComponent as TabHomeIcon } from 'assets/svg/tab_home.svg';
import { ReactComponent as TabCalendarIcon } from 'assets/svg/tab_calendar.svg';
import { ReactComponent as TabCommunityIcon } from 'assets/svg/tab_community.svg';
import { ReactComponent as TabEventsIcon } from 'assets/svg/tab_events.svg';
import { ReactComponent as TabGroupIcon } from 'assets/svg/tab_groups.svg';
import { ReactComponent as TabBlogIcon } from 'assets/svg/tab_blog.svg';
import styled from 'styled-components';
import { Container, Button } from 'components/elements';
import { Drawer } from 'antd';
import { Link, useHistory, useLocation } from 'react-router-dom';
import ForgotPasswordModal from 'components/forgotPassword';
import { logout } from 'services/auth';
import { useLoggedInStateQuery, useGetMyAccountLazyQuery } from 'graphql/types';
import { colors } from 'settings/style';
import {
  DownOutlined,
} from '@ant-design/icons';
import {
  HeaderWrapper,
  MenuLink,
  LogoDesktopImg,
  LogoMobileImg,
  ProfileDropdownWrapper,
  ProfileButton,
  UserAvatarIcon,
  AvatarImg,
  ProfileMenu,
  MenuItem,
} from './index.styled';

const StyledTabHomeIcon = styled(TabHomeIcon) <{ selected: boolean }>`
  path {
    fill: ${props => props.selected ? colors.primary500 : colors.gray600} !important;
  }
`;
const StyledTabCalendarIcon = styled(TabCalendarIcon) <{ selected: boolean }>`
  path {
    fill: ${props => props.selected ? colors.primary500 : colors.gray600} !important;
  }
`;

const StyledTabGroupsIcon = styled(TabGroupIcon) <{ selected: boolean }>`
  path {
    fill: ${props => props.selected ? colors.primary500 : colors.gray600} !important;
  }
`;

const StyledTabEventsIcon = styled(TabEventsIcon) <{ selected: boolean }>`
  path {
    fill: ${props => props.selected ? colors.primary500 : colors.gray600} !important;
  }
`;

const StyledTabCommunityIcon = styled(TabCommunityIcon) <{ selected: boolean }>`
  path {
    fill: ${props => props.selected ? colors.primary500 : colors.gray600} !important;
  }
`;

const StyledTabBlogIcon = styled(TabBlogIcon) <{ selected: boolean }>`
  path {
    fill: ${props => props.selected ? colors.primary500 : colors.gray600} !important;
  }
`;

const getHeaderRoutes = () => [
  { name: 'Home', path: '/', icon: <TabHomeIcon /> },
  { name: 'Calendar', path: '/calendar', icon: <TabCalendarIcon /> },
  { name: 'Groups', path: '/groups', icon: <TabGroupIcon /> },
  { name: 'Events', path: '/events', icon: <TabEventsIcon /> },
  // { name: 'Community', path: '/community', icon: <TabCommunityIcon /> },
  { name: 'Blogs', path: '/community', icon: <TabCommunityIcon /> },
  { name: 'Messages', path: '/messages', icon: <TabBlogIcon /> },
];

function isGroupsNavPath(pathname: string) {
  return (
    pathname === '/groups' ||
    pathname === '/groups/create' ||
    pathname.startsWith('/groups/update/') ||
    pathname.startsWith('/groups/manage/')
  );
}

function isEventsNavPath(pathname: string) {
  return (
    pathname === '/events' ||
    pathname === '/events/create' ||
    pathname.startsWith('/events/update/') ||
    pathname.startsWith('/events/bookings/')
  );
}

export default function Header({ light = true }) {
  const useLoggedInStateResult = useLoggedInStateQuery();
  const isLoggedIn = useLoggedInStateResult.data?.loggedInState;
  const headerRoutes = getHeaderRoutes();
  const history = useHistory();
  const location = useLocation();

  const mobileTabActiveKey =
    location.pathname.startsWith('/messages') || location.pathname === '/message'
      ? '/messages'
      : isGroupsNavPath(location.pathname)
        ? '/groups'
        : isEventsNavPath(location.pathname)
          ? '/events'
          : location.pathname;

  const [drawer, setDrawer] = useState(false);
  const [profileDropdown, setProfileDropdown] = useState(false);
  const [scrollPos, setScrollPos] = useState(0);
  const profileDropdownRef = useRef<HTMLDivElement | null>(null);
  const [getMyProfile, { data: profileData }] = useGetMyAccountLazyQuery();

  const setRouteActive = (value: string) => {
    if ((value === '/groups' || value === '/events' || value === '/messages' || value === '/community') && !isLoggedIn) {
      history.push('/login');
      return;
    }
    history.push(value);
  };

  useEffect(() => {
    if (isLoggedIn) {
      getMyProfile();
    }
  }, [getMyProfile, isLoggedIn]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!profileDropdownRef.current) return;
      if (!profileDropdownRef.current.contains(event.target as Node)) {
        setProfileDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setScrollPos(window.scrollY || 0);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  const onSignup = () => {
    history.push('/signup');
  };

  const onLogin = () => {
    history.push('/login');
  };

  return (
    <HeaderWrapper light={light} isLoggedIn={isLoggedIn} scrollPos={scrollPos}>
      <Drawer title="Basic Drawer" placement="left" closable={false} onClose={() => setDrawer(false)} open={drawer}>
        <Menu light={light} routes={headerRoutes} isLoggedIn={isLoggedIn} />
      </Drawer>
      <div className="mobile-menu">
        <TabBar onChange={(value) => setRouteActive(value)} activeKey={mobileTabActiveKey}>
          {headerRoutes.map((item) => (
            <TabBar.Item
              key={item.path}
              icon={
                item.path === '/' ? (
                  <StyledTabHomeIcon selected={location.pathname === item.path} />
                ) : item.path === '/calendar' ? (
                  <StyledTabCalendarIcon selected={location.pathname === item.path} />
                ) : item.path === '/groups' ? (
                  <StyledTabGroupsIcon selected={isGroupsNavPath(location.pathname)} />
                ) : item.path === '/events' ? (
                  <StyledTabEventsIcon selected={isEventsNavPath(location.pathname)} />
                ) : item.path === '/community' ? (
                  <StyledTabCommunityIcon selected={location.pathname === item.path} />
                ) : item.path === '/messages' ? (
                  <StyledTabBlogIcon
                    selected={
                      location.pathname.startsWith('/messages') || location.pathname === '/message'
                    }
                  />
                ) : (
                  item.icon
                )
              }
              title={item.name}
            />
          ))}
        </TabBar>
        <SafeArea position="bottom" />
      </div>

      <Container>
        <ForgotPasswordModal />
        <div className="header-row">
          {/* <Button type="link" className="hamburger-menu" onClick={() => setDrawer(true)}>
            <ThreeBarsIcon size="medium" />
          </Button> */}
          <div className="logo-cont">
            <Link to="/">
              <LogoDesktopImg src={LogoDesktop} alt="polgozar" />
              <LogoMobileImg src={LogoMobile} alt="polgozar" />
            </Link>
          </div>
          <nav className="menu-container">
            <Menu light={light} routes={headerRoutes} isLoggedIn={isLoggedIn} />
            <div className="login-signup">
              {!isLoggedIn && (
                <Button className="btn-signup" type="primary" onClick={onSignup}>
                  Sign up
                </Button>
              )}
              {isLoggedIn ? (
                <ProfileDropdownWrapper ref={profileDropdownRef}>
                  <ProfileButton onClick={() => setProfileDropdown(!profileDropdown)}>
                    {profileData?.myAccount?.data.attributes.avatarUrl ? (
                      <AvatarImg src={profileData.myAccount.data.attributes.avatarUrl} alt="avatar" />
                    ) : (
                      <UserAvatarIcon />
                    )}
                    <span>{profileData?.myAccount?.data.attributes.firstname || ''}</span>
                    <DownOutlined size={12} />
                  </ProfileButton>
                  {profileDropdown && (
                    <ProfileMenu>
                      <MenuItem onClick={() => {
                        history.push('/account');
                        setProfileDropdown(false);
                      }}>
                        My Profile
                      </MenuItem>
                      <MenuItem onClick={() => {
                        logout();
                        setProfileDropdown(false);
                      }}>
                        Logout
                      </MenuItem>
                    </ProfileMenu>
                  )}
                </ProfileDropdownWrapper>
              ) : (
                <Button className="btn-signin" type="primary" onClick={onLogin}>
                  Sign in
                </Button>
              )}
            </div>
          </nav>
        </div>
      </Container >
    </HeaderWrapper >
  );
}

interface MenuProps {
  light: boolean;
  routes: { name: string; path: string; icon: JSX.Element }[];
  isLoggedIn?: boolean;
}

function isRouteActive(routePath: string) {
  return (_match: unknown, loc: { pathname: string }) => {
    if (!loc) return false;
    const { pathname } = loc;
    if (routePath === '/groups') {
      return isGroupsNavPath(pathname);
    }
    if (routePath === '/events') {
      return isEventsNavPath(pathname);
    }
    if (routePath === '/messages') {
      return (
        pathname === '/messages' ||
        pathname.startsWith('/messages/') ||
        pathname === '/message'
      );
    }
    return pathname === routePath;
  };
}

const Menu = ({ light, routes, isLoggedIn }: MenuProps) => {
  const history = useHistory();

  return (
    <ul className="menu-links">
      {routes.map((item) => (
        <li className="menu-links__item" key={item.name}>
          <MenuLink
            isActive={isRouteActive(item.path)}
            to={item.path}
            activeClassName="selected"
            onClick={(e) => {
              if (
                (item.path === '/groups' || item.path === '/events' || item.path === '/messages' || item.path === '/community') &&
                !isLoggedIn
              ) {
                e.preventDefault();
                history.push('/login');
              }
            }}
          >
            {item.name}
          </MenuLink>
        </li>
      ))}
    </ul>
  );
};